import customerRecordDesktopGif from '@/asset/image/tour-customer-record-desktop.gif'
import customerRecordMobileGif from '@/asset/image/tour-customer-record-mobile.gif'
import navigationDesktopGif from '@/asset/image/tour-navigation-desktop.gif'
import navigationMobileGif from '@/asset/image/tour-navigation-mobile.gif'
import storeAvailabilityDesktopGif from '@/asset/image/tour-store-availability-desktop.gif'
import storeAvailabilityMobileGif from '@/asset/image/tour-store-availability-mobile.gif'
import { defaultRouteForPlatform } from '@/services/platformRouting'

export const navTourTarget = (route, part) => (part ? `nav:${route}:${part}` : `nav:${route}`)
export const navItemTarget = (link, part) => (part ? `nav-item:${link}:${part}` : `nav-item:${link}`)
export const pageTabTarget = id => `page-tab:${id}`
export const PAGE_TABS_MORE = 'page-tabs:more'

const onTarget = target => `[data-tour="${target}"]`

const SELL = '/sell'
const STORES = '/store/stores'

const OPEN_MENU = {
  id: 'open-menu',
  element: onTarget('menu'),
  side: 'bottom',
  align: 'start',
  advanceWhenNextAppears: true,
  buttons: ['close'],
  title: 'Your Menu Lives Here',
  description: 'Every part of your dashboard is a tap away.',
  action: 'Tap the menu to open it'
}

const navigationSteps = ({ where, verb, placement, media, opening = [] }) => [
  {
    id: 'intro',
    modal: true,
    media,
    title: 'A New Way Around Your Dashboard',
    description: `The ${where} now groups everything into sections, and long pages are split into tabs. Here's a quick look - it takes under a minute.`,
    nextLabel: 'Show Me'
  },
  ...opening,
  {
    id: 'sell',
    element: onTarget(navTourTarget(SELL)),
    ...placement,
    advanceWhenNextAppears: true,
    buttons: ['close'],
    title: `Sections Open in the ${where === 'menu' ? 'Menu' : 'Sidebar'}`,
    description: "Each section expands to show what's inside.",
    action: `${verb} Sell to see how`
  },
  {
    id: 'stores',
    branch: 'stores',
    element: onTarget(navItemTarget(STORES)),
    ...placement,
    advanceWhenNextAppears: true,
    buttons: ['close'],
    title: 'Everything You Sell, Grouped',
    description: 'Stores, products, discounts and settings each have a place under Sell.',
    action: `${verb} Stores to see yours`
  },
  {
    id: 'store',
    branch: 'stores',
    element: `${onTarget(navItemTarget(STORES, 'items'))} > a[href^="${STORES}/"]`,
    ...placement,
    advanceOnClick: true,
    buttons: ['close'],
    title: 'Open a Store',
    description: 'Store settings used to be one long page.',
    action: `${verb} a store to see how it looks now`
  },
  {
    id: 'tabs',
    branch: 'stores',
    element: onTarget('page-tabs'),
    side: 'bottom',
    align: 'start',
    title: 'Long Pages Are Now Tabs',
    description: 'The settings are split into tabs along the top, so you can go straight to the part you need.',
    nextLabel: 'Done'
  },
  {
    id: 'sell-items',
    branch: 'menu',
    element: onTarget(navTourTarget(SELL, 'items')),
    ...placement,
    title: 'Everything You Sell, Grouped',
    description: 'Your menu page and the rest of what you sell each have a place under Sell.',
    nextLabel: 'Done'
  }
]

export const navigationTour = {
  id: 'navigation',
  flag: 'dashboard-navigation-tour',
  theme: 'dark',
  home: platform => defaultRouteForPlatform(platform, '/orders'),
  buttons: ['next', 'close'],
  branches: {
    stores: onTarget(navItemTarget(STORES)),
    menu: onTarget(navItemTarget(SELL))
  },
  steps: navigationSteps({
    where: 'sidebar',
    verb: 'Click',
    placement: { side: 'right', align: 'start' },
    media: { src: navigationDesktopGif, width: 600, height: 470, alt: 'Opening Sell, then Stores, then a store' }
  }),
  mobileSteps: navigationSteps({
    where: 'menu',
    verb: 'Tap',
    placement: { side: 'bottom', align: 'start' },
    media: { src: navigationMobileGif, width: 390, height: 500, alt: 'Opening the menu, then Sell, then a store' },
    opening: [OPEN_MENU]
  })
}

const AVAILABILITY_TAB = 'availability'
const STORE_EDITOR_PAGE = /^\/store\/stores\/\d+(\/[^/]+)?\/?$/
const TAB_BAR = { side: 'bottom', align: 'start' }

const storeAvailabilitySteps = ({ verb, media }) => [
  {
    id: 'intro',
    modal: true,
    media,
    title: 'Product Availability Has Its Own Tab',
    description:
      'Product availability used to sit under Product selection. Now it has a tab of its own on every store.',
    nextLabel: 'Show Me'
  },
  {
    id: 'tab',
    branch: 'tab',
    element: onTarget(pageTabTarget(AVAILABILITY_TAB)),
    ...TAB_BAR,
    advanceOnClick: true,
    buttons: ['close'],
    title: 'Right After Product Selection',
    description: 'Look for it here whenever you open a store.',
    action: `${verb} Product availability`
  },
  {
    id: 'more',
    branch: 'more',
    element: onTarget(PAGE_TABS_MORE),
    ...TAB_BAR,
    advanceWhenNextAppears: true,
    buttons: ['close'],
    title: 'Find It Under More',
    description: "Tabs that don't fit on your screen are grouped under More.",
    action: `${verb} More, then Product availability`
  },
  {
    id: 'availability',
    element: `${onTarget('store-availability')} ${onTarget('settings-section:text')}`,
    ...TAB_BAR,
    title: 'Choose When Products Are Available',
    description: `Set the days each product is available and how many you can sell. ${verb} a product to change it.`,
    nextLabel: 'Done'
  }
]

export const storeAvailabilityTour = {
  id: 'store-availability',
  flag: 'dashboard-store-availability-tour',
  theme: 'dark',
  startsOn: route => STORE_EDITOR_PAGE.test(route.path),
  buttons: ['next', 'close'],
  branches: {
    more: `${onTarget(PAGE_TABS_MORE)}[data-hidden-tabs~="${AVAILABILITY_TAB}"]`,
    tab: onTarget(pageTabTarget(AVAILABILITY_TAB))
  },
  steps: storeAvailabilitySteps({
    verb: 'Click',
    media: {
      src: storeAvailabilityDesktopGif,
      width: 600,
      height: 470,
      alt: 'Opening the Product availability tab on a store'
    }
  }),
  mobileSteps: storeAvailabilitySteps({
    verb: 'Tap',
    media: {
      src: storeAvailabilityMobileGif,
      width: 390,
      height: 500,
      alt: 'Opening More, then Product availability on a store'
    }
  })
}

export const onCustomerRecord = route => !!route?.meta?.customerTab && !route?.meta?.customerView

const TABS_COPY = {
  title: 'The Record Is Now in Tabs',
  description: 'Their messages, profile and the rest each have their own tab, instead of one long page.'
}

const customerRecordSteps = ({ media, verb, revenuePlacement }) => [
  {
    id: 'intro',
    modal: true,
    media,
    title: 'The Customer Record Has Changed',
    description:
      "A profile now opens with what the customer is worth and a year of their sales, and the rest sits in tabs. Here's a quick look.",
    nextLabel: 'Show Me'
  },
  {
    id: 'tabs',
    branch: 'profile',
    element: onTarget('customer-tabs'),
    side: 'bottom',
    align: 'start',
    ...TABS_COPY
  },
  {
    id: 'open-profile',
    branch: 'record',
    element: onTarget('customer-tabs'),
    side: 'bottom',
    align: 'start',
    advanceWhenNextAppears: true,
    buttons: ['close'],
    ...TABS_COPY,
    action: `${verb} Profile to see what changed`
  },
  {
    id: 'lifetime',
    element: onTarget('customer-lifetime'),
    side: 'bottom',
    align: 'start',
    title: 'What They Are Worth, Up Top',
    description: 'Lifetime value, paid orders, average order and how long they have been a customer.'
  },
  {
    id: 'revenue',
    element: onTarget('customer-revenue'),
    ...revenuePlacement,
    title: 'A Year of Their Sales',
    description: `The chart shows what they spent each month and how often they order. ${verb} Open full report for the detail, with your own date range, a breakdown by period and a CSV export.`,
    nextLabel: 'Done'
  }
]

export const customerRecordTour = {
  id: 'customer-record',
  flag: 'dashboard-customer-record-tour',
  theme: 'dark',
  startsOn: onCustomerRecord,
  buttons: ['next', 'close'],
  branches: {
    profile: onTarget('customer-lifetime'),
    record: onTarget('customer-tabs')
  },
  steps: customerRecordSteps({
    media: {
      src: customerRecordDesktopGif,
      width: 896,
      height: 600,
      alt: 'A customer profile with lifetime value, a sales chart and tabs'
    },
    verb: 'Click',
    revenuePlacement: { side: 'left', align: 'start' }
  }),
  mobileSteps: customerRecordSteps({
    media: {
      src: customerRecordMobileGif,
      width: 390,
      height: 500,
      alt: 'A customer profile with lifetime value, scrolling down to the sales chart'
    },
    verb: 'Tap',
    revenuePlacement: { side: 'top', align: 'center' }
  })
}

export const tours = [navigationTour, storeAvailabilityTour, customerRecordTour]

export const stepsForBranch = (steps, branch) => steps.filter(step => !step.branch || step.branch === branch)

export const progressSteps = steps => steps.filter(step => !step.modal)

export function chooseBranch(tour, root = document) {
  return Object.keys(tour.branches ?? {}).find(name => root.querySelector(tour.branches[name])) ?? null
}

export function availableSteps(tour, root = document) {
  return tour.steps.filter(step => !step.element || root.querySelector(step.element))
}
